import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { invalidateSeoCache } from "@/components/SEO";
import { Save, Globe, Search, BarChart3, FileCode2, Lock, RefreshCw, Radar } from "lucide-react";

export default function SeoSettingsPage() {
  const [tab, setTab] = useState("defaults");
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  // Registry of routes discovered at runtime — hydrated from the backend, so
  // any new page that mounts the <SEO pageKey="..."> component shows up here
  // automatically without a code change to this file.
  const [pages, setPages] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  // Owner gate — call before hydrating settings so trial / member users
  // never see the underlying data or forms.
  const [access, setAccess] = useState(null);

  const loadPages = async () => {
    setPagesLoading(true);
    try {
      const r = await api.get("/seo/pages");
      setPages(r.data || []);
    } catch (e) {
      // Editor still usable without the registry — but surface the failure so
      // devs see it in the console instead of silently swallowing it.
      console.debug("[SeoSettingsPage] Failed to load /seo/pages:", e);
    } finally {
      setPagesLoading(false);
    }
  };

  useEffect(() => {
    api.get("/seo/access")
      .then((r) => {
        setAccess(r.data);
        if (r.data.allowed) {
          api.get("/seo/settings").then((rr) => setSettings({ pages: {}, ...rr.data })).catch(() => setSettings({ pages: {} }));
          loadPages();
        }
      })
      .catch(() => setAccess({ allowed: false, reason: "unknown" }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...settings };
      payload.pages = payload.pages || {};
      await api.put("/seo/settings", payload);
      invalidateSeoCache();
      toast.success("SEO settings saved. Live everywhere.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setSaving(false); }
  };

  if (access === null) return <div className="p-10 text-[#8A94A6]">Loading…</div>;
  if (!access.allowed) {
    const copy = access.reason === "member"
      ? "You're signed in as a workspace member. Site-wide SEO settings (meta tags, GA4, GSC verification, robots.txt) are managed by the workspace owner — ask them to update these from their own account."
      : "Site-wide SEO settings — meta tags, GA4, GSC verification, robots.txt — are only editable by workspace owners. Trial and member accounts can't access this section.";
    return (
      <div className="p-8 lg:p-10 max-w-3xl" data-testid="seo-restricted">
        <div className="text-xs text-[#8A94A6] uppercase tracking-wider font-medium">SEO settings</div>
        <h1 className="mt-2 text-3xl font-display font-bold text-[#0A0D14]">Owner-only section.</h1>
        <div className="mt-8 card-crisp p-6 flex items-start gap-4">
          <div className="h-10 w-10 rounded-md bg-amber-50 text-amber-700 flex items-center justify-center shrink-0"><Lock className="h-5 w-5" /></div>
          <div>
            <div className="text-sm font-medium text-[#0A0D14]">Locked</div>
            <p className="mt-1 text-sm text-[#525B6A]">{copy}</p>
          </div>
        </div>
      </div>
    );
  }
  if (!settings) return <div className="p-10 text-[#8A94A6]">Loading…</div>;

  const setPage = (key, field, val) => {
    const next = { ...(settings.pages || {}) };
    next[key] = { ...(next[key] || {}), [field]: val };
    if (!val) delete next[key][field];
    if (Object.keys(next[key] || {}).length === 0) delete next[key];
    setSettings({ ...settings, pages: next });
  };

  const tabBtn = (id, label, Icon) => (
    <button data-testid={`seo-tab-${id}`} onClick={() => setTab(id)} className={`px-3 py-2 text-sm rounded-md inline-flex items-center gap-1.5 border ${tab === id ? 'bg-[#0047FF] text-white border-[#0047FF]' : 'bg-white text-[#525B6A] border-[#E2E8F0] hover:text-[#0A0D14]'}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div className="p-8 lg:p-10 max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs text-[#8A94A6] uppercase tracking-wider font-medium">Search & discovery</div>
          <h1 className="mt-2 text-3xl font-display font-bold text-[#0A0D14]">SEO settings.</h1>
          <p className="mt-2 text-sm text-[#525B6A] max-w-2xl">Control what search engines and social platforms show for SerpNex. Changes take effect instantly on every marketing page.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="seo-save" className="btn-primary px-4 py-2.5 rounded-md text-sm inline-flex items-center gap-2">
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {tabBtn("defaults", "Site defaults", Globe)}
        {tabBtn("pages", "Per-page", Search)}
        {tabBtn("analytics", "Analytics & verification", BarChart3)}
        {tabBtn("robots", "Robots.txt", FileCode2)}
      </div>

      {tab === "defaults" && (
        <section className="mt-6 card-crisp p-6 space-y-4" data-testid="seo-defaults">
          <Field label="Site name" value={settings.site_name} onChange={(v) => setSettings({ ...settings, site_name: v })} placeholder="SerpNex" testid="seo-site-name" />
          <Field label="Default page title" value={settings.default_title} onChange={(v) => setSettings({ ...settings, default_title: v })} placeholder="SerpNex — Transparent Keyword Rank Tracking" testid="seo-default-title" hint={`${(settings.default_title || '').length} / 60 chars recommended`} />
          <Field label="Default meta description" value={settings.default_description} onChange={(v) => setSettings({ ...settings, default_description: v })} placeholder="One sentence describing SerpNex." testid="seo-default-desc" multiline hint={`${(settings.default_description || '').length} / 160 chars recommended`} />
          <Field label="Default OG / social image URL" value={settings.default_og_image || ""} onChange={(v) => setSettings({ ...settings, default_og_image: v })} placeholder="https://…/og-image.png" testid="seo-og-image" />
          <Field label="Twitter handle" value={settings.twitter_handle || ""} onChange={(v) => setSettings({ ...settings, twitter_handle: v })} placeholder="@serpnex" testid="seo-twitter" />
        </section>
      )}

      {tab === "pages" && (
        <section className="mt-6" data-testid="seo-pages">
          <div className="mb-4 flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-2 text-xs text-[#525B6A] max-w-2xl">
              <Radar className="h-3.5 w-3.5 text-[#0047FF] mt-0.5 shrink-0" />
              <span>Routes are discovered automatically the first time anyone visits them. Add a new page in the code, load it once, and it will appear here — no config file to edit. Leave any field blank to fall back to the site default.</span>
            </div>
            <button data-testid="seo-pages-refresh" onClick={loadPages} disabled={pagesLoading} className="btn-ghost px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1.5">
              <RefreshCw className={`h-3 w-3 ${pagesLoading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>

          {pages.length === 0 ? (
            <div className="card-crisp p-8 text-center" data-testid="seo-pages-empty">
              <div className="text-sm text-[#525B6A]">No routes discovered yet.</div>
              <div className="mt-1 text-xs text-[#8A94A6]">Visit any public page (Home, Pricing, Features, etc.) to auto-register it here.</div>
            </div>
          ) : (
            <div className="card-crisp overflow-hidden">
              <div className="grid grid-cols-12 px-4 py-3 bg-[#F8F9FA] border-b border-[#E2E8F0] text-[10px] uppercase tracking-wider text-[#8A94A6] font-medium">
                <div className="col-span-3">Page</div>
                <div className="col-span-3">Title override</div>
                <div className="col-span-3">Description override</div>
                <div className="col-span-3">Canonical URL</div>
              </div>
              {pages.map((p) => {
                const over = (settings.pages && settings.pages[p.key]) || {};
                return (
                  <div key={p.key} className="grid grid-cols-12 items-start px-4 py-3 border-b border-[#E2E8F0] gap-3">
                    <div className="col-span-3">
                      <div className="text-sm text-[#0A0D14] font-medium">{p.label || p.key}</div>
                      <div className="text-[11px] text-[#8A94A6] font-mono">{p.path}</div>
                      {p.default_title && (
                        <div className="mt-1 text-[10px] text-[#8A94A6] truncate" title={p.default_title}>Auto: {p.default_title}</div>
                      )}
                    </div>
                    <div className="col-span-3">
                      <input data-testid={`seo-page-title-${p.key}`} value={over.title || ""} onChange={(e) => setPage(p.key, "title", e.target.value)} placeholder="(uses default)" className="w-full px-2 py-1.5 border border-[#E2E8F0] rounded text-sm" />
                      <div className="mt-1 text-[10px] text-[#8A94A6]">{(over.title || "").length}/60</div>
                    </div>
                    <div className="col-span-3">
                      <textarea data-testid={`seo-page-desc-${p.key}`} value={over.description || ""} onChange={(e) => setPage(p.key, "description", e.target.value)} placeholder="(uses default)" rows={2} className="w-full px-2 py-1.5 border border-[#E2E8F0] rounded text-sm" />
                      <div className="mt-1 text-[10px] text-[#8A94A6]">{(over.description || "").length}/160</div>
                    </div>
                    <div className="col-span-3">
                      <input data-testid={`seo-page-canonical-${p.key}`} value={over.canonical || ""} onChange={(e) => setPage(p.key, "canonical", e.target.value)} placeholder={`https://serpnex.com${p.path}`} className="w-full px-2 py-1.5 border border-[#E2E8F0] rounded text-sm font-mono" />
                      <div className="mt-1 text-[10px] text-[#8A94A6]">Leave blank to auto-derive from route</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {tab === "analytics" && (
        <section className="mt-6 card-crisp p-6 space-y-4" data-testid="seo-analytics">
          <Field label="Google Analytics 4 (Measurement ID)" value={settings.ga4_id || ""} onChange={(v) => setSettings({ ...settings, ga4_id: v.trim() })} placeholder="G-XXXXXXXX" testid="seo-ga4" hint="Injects gtag.js on every marketing page. Leave blank to disable." />
          <Field label="Google Search Console verification code" value={settings.gsc_verification || ""} onChange={(v) => setSettings({ ...settings, gsc_verification: v.trim() })} placeholder="Content value from the meta-tag method" testid="seo-gsc" hint="Only the content, not the full <meta> tag." />
        </section>
      )}

      {tab === "robots" && (
        <section className="mt-6 card-crisp p-6 space-y-3" data-testid="seo-robots">
          <div className="text-xs text-[#525B6A]">Contents of <code className="text-[#0A0D14]">/api/robots.txt</code>. Leave blank to use the default (allow all, disallow /dashboard).</div>
          <textarea
            data-testid="seo-robots-txt"
            value={settings.robots_txt || ""}
            onChange={(e) => setSettings({ ...settings, robots_txt: e.target.value })}
            rows={10}
            placeholder={"User-agent: *\nAllow: /\nDisallow: /dashboard\nSitemap: https://serpnex.com/api/sitemap.xml"}
            className="w-full font-mono text-xs px-3 py-2 border border-[#E2E8F0] rounded-md"
          />
        </section>
      )}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid, multiline, hint }) {
  return (
    <div>
      <label className="text-xs font-medium text-[#525B6A] mb-1 block">{label}</label>
      {multiline ? (
        <textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={3} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
      ) : (
        <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2 border border-[#E2E8F0] rounded-md text-sm" />
      )}
      {hint && <div className="mt-1 text-[11px] text-[#8A94A6]">{hint}</div>}
    </div>
  );
}
